import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { SelectProps } from '@amzn/awsui-components-console/select';
import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';
import { TrackFiltersState } from '../types';
import {
  useListNamespacesQuery,
  useListStringGroupsQuery,
} from 'src/generated/graphql';
import {
  useSavedTrackSearches,
  SavedTrackSearchState,
  generateTrackSearchLabel,
} from './useSavedTrackSearches';

const DEFAULT_DATE_RANGE: DateRangePickerProps.Value = {
  type: 'relative',
  amount: 30,
  unit: 'day',
};

const DEFAULT_STRING_GROUP_OPTIONS: SelectProps.Options = [
  { value: '', label: 'All' },
];

/**
 * Maximum number of pagination batches to fetch for dropdowns.
 * At 100 items/page this caps at ~5000 options — adequate for our use case.
 * Beyond this, users should use the dropdown's type-ahead filter.
 */
const MAX_PAGINATION_BATCHES = 50;

export function useTrackFilters() {
  const { save: saveTrackSearchApi } = useSavedTrackSearches();
  const [namespace, setNamespace] = useState<SelectProps.Option | null>(null);
  const [stringGroup, setStringGroup] = useState<SelectProps.Option | null>(
    null,
  );
  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(
    DEFAULT_DATE_RANGE,
  );
  const [appliedFilters, setAppliedFilters] = useState<TrackFiltersState>({
    namespace: '',
    stringGroup: '',
    dateRange: DEFAULT_DATE_RANGE,
    applyGeneration: 0,
  });

  // --- Namespace pagination with batch cap ---
  const namespaceBatchCount = useRef(0);

  const {
    data: namespacesData,
    loading: namespacesLoading,
    error: namespacesError,
    fetchMore: fetchMoreNamespaces,
  } = useListNamespacesQuery({
    variables: { pageSize: 100 },
  });

  useEffect(() => {
    const nextToken = namespacesData?.listNamespaces?.nextToken;
    if (
      nextToken &&
      !namespacesLoading &&
      namespaceBatchCount.current < MAX_PAGINATION_BATCHES
    ) {
      namespaceBatchCount.current += 1;
      fetchMoreNamespaces({
        variables: { nextToken },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            listNamespaces: {
              ...fetchMoreResult.listNamespaces,
              namespaces: [
                ...prev.listNamespaces.namespaces,
                ...fetchMoreResult.listNamespaces.namespaces,
              ],
            },
          };
        },
      }).catch(() => {
        // Pagination failure — partial list is still usable
      });
    }
  }, [namespacesData, namespacesLoading, fetchMoreNamespaces]);

  const namespaceOptions: SelectProps.Options = useMemo(() => {
    const namespaces = namespacesData?.listNamespaces?.namespaces ?? [];
    return namespaces.map((ns) => ({
      value: ns.namespaceId,
      label: ns.namespaceId,
    }));
  }, [namespacesData]);

  // --- String group pagination with batch cap + stale-response guard ---
  const stringGroupBatchCount = useRef(0);
  const stringGroupNamespaceRef = useRef<string | null>(null);

  const {
    data: stringGroupsData,
    loading: stringGroupsLoading,
    error: stringGroupsError,
    fetchMore: fetchMoreStringGroups,
  } = useListStringGroupsQuery({
    variables: { namespaceId: namespace?.value ?? '', pageSize: 100 },
    skip: !namespace?.value,
  });

  // Reset batch counter when namespace changes
  useEffect(() => {
    stringGroupBatchCount.current = 0;
    stringGroupNamespaceRef.current = namespace?.value ?? null;
  }, [namespace]);

  useEffect(() => {
    const nextToken = stringGroupsData?.listStringGroups?.nextToken;
    // Stale-response guard: ignore responses from a previous namespace
    const currentNs = namespace?.value ?? null;
    if (currentNs !== stringGroupNamespaceRef.current) return;

    if (
      nextToken &&
      !stringGroupsLoading &&
      stringGroupBatchCount.current < MAX_PAGINATION_BATCHES
    ) {
      stringGroupBatchCount.current += 1;
      fetchMoreStringGroups({
        variables: { namespaceId: currentNs ?? '', nextToken },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            listStringGroups: {
              ...fetchMoreResult.listStringGroups,
              stringGroups: [
                ...prev.listStringGroups.stringGroups,
                ...fetchMoreResult.listStringGroups.stringGroups,
              ],
            },
          };
        },
      }).catch(() => {
        // Pagination failure — partial list is still usable
      });
    }
  }, [stringGroupsData, stringGroupsLoading, fetchMoreStringGroups, namespace]);

  const stringGroupOptions: SelectProps.Options = useMemo(() => {
    if (!namespace?.value) return DEFAULT_STRING_GROUP_OPTIONS;
    const groups = stringGroupsData?.listStringGroups?.stringGroups ?? [];
    return [
      { value: '', label: 'All' },
      ...groups.map((g) => ({
        value: g.stringGroupId,
        label: g.stringGroupId,
      })),
    ];
  }, [namespace, stringGroupsData]);

  // Auto-select "All" for string group when namespace changes
  const handleNamespaceChange = useCallback(
    (selected: SelectProps.Option | null) => {
      setNamespace(selected);
      setStringGroup({ value: '', label: 'All' });
    },
    [],
  );

  const applyFilters = useCallback(() => {
    setAppliedFilters((prev) => ({
      namespace: namespace?.value ?? '',
      stringGroup: stringGroup?.value ?? '',
      dateRange,
      applyGeneration: prev.applyGeneration + 1,
    }));
  }, [namespace, stringGroup, dateRange]);

  // --- Saved searches (persisted in users DDB table via UserPreference API) ---

  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');

  /** The selection a save would capture — the draft filters, not the applied ones. */
  const currentSearchState: SavedTrackSearchState = useMemo(
    () => ({
      namespace: namespace?.value ?? '',
      stringGroup: stringGroup?.value ?? '',
      dateRange,
    }),
    [namespace, stringGroup, dateRange],
  );

  const openSaveModal = useCallback(() => {
    // Pre-fill with a generated label as a suggestion, as the Search page does.
    setSaveSearchName(generateTrackSearchLabel(currentSearchState));
    setDuplicateError('');
    setSaveModalVisible(true);
  }, [currentSearchState]);

  const confirmSaveSearch = useCallback(async () => {
    if (saveSearchName.trim().length === 0) return;
    setIsSaving(true);
    setDuplicateError('');
    try {
      const result = await saveTrackSearchApi(
        currentSearchState,
        saveSearchName,
      );
      if (result.success) {
        setSaveModalVisible(false);
        setSaveSearchName('');
      } else if (result.duplicateLabel) {
        setDuplicateError(
          `A saved search with this name and filters already exists.`,
        );
      } else if (result.loadFailed) {
        setDuplicateError(
          'Could not load saved searches. Please refresh the page.',
        );
      } else if (result.notReady) {
        setDuplicateError('Still loading, please try again.');
      } else {
        setDuplicateError('Failed to save. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [saveSearchName, currentSearchState, saveTrackSearchApi]);

  const cancelSaveSearch = useCallback(() => {
    setSaveModalVisible(false);
    setSaveSearchName('');
    setDuplicateError('');
  }, []);

  /**
   * Restores a saved selection and applies it in one step.
   *
   * Sets appliedFilters directly rather than calling applyFilters, because that
   * reads the draft state from its closure — which still holds the previous
   * values on this render — and would apply stale filters.
   */
  const loadSavedSearch = useCallback((saved: SavedTrackSearchState) => {
    setNamespace({ value: saved.namespace, label: saved.namespace });
    setStringGroup({
      value: saved.stringGroup,
      label: saved.stringGroup || 'All',
    });
    setDateRange(saved.dateRange);
    setAppliedFilters((prev) => ({
      namespace: saved.namespace,
      stringGroup: saved.stringGroup,
      dateRange: saved.dateRange,
      applyGeneration: prev.applyGeneration + 1,
    }));
  }, []);

  return {
    namespace,
    setNamespace: handleNamespaceChange,
    stringGroup,
    setStringGroup,
    dateRange,
    setDateRange,
    appliedFilters,
    applyFilters,
    namespaceOptions,
    stringGroupOptions,
    namespacesLoading,
    stringGroupsLoading,
    namespacesError,
    stringGroupsError,

    // Saved searches
    saveModalVisible,
    saveSearchName,
    setSaveSearchName,
    openSaveModal,
    confirmSaveSearch,
    cancelSaveSearch,
    loadSavedSearch,
    isSaving,
    duplicateError,
  };
}
